import React, { useState, useEffect } from 'react';
import './CharacterEdit.css'; /* Reuses edit styles */
import { influencerService, getInfluencerImageUrl } from '../services/influencerApi';

function EnvironmentEdit({ editData, onNavigate }) {
  const originalPath = editData?.path;
  const environmentName = editData?.name;
  const filename = editData?.filename;

  const [prompt, setPrompt] = useState('');
  const [strength, setStrength] = useState(0.7);
  const [isEditing, setIsEditing] = useState(false);
  const [editedImagePath, setEditedImagePath] = useState(null);
  const [error, setError] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [enhancedEditPrompt, setEnhancedEditPrompt] = useState('');

  useEffect(() => {
    if (!originalPath || !environmentName || !filename) {
      if (onNavigate) onNavigate('environment-library');
    }
  }, [originalPath, environmentName, filename, onNavigate]);

  const handleEditImage = async () => {
    if (!prompt.trim() || !originalPath) return;

    try {
      setIsEditing(true);
      setError('');
      setEditedImagePath(null);
      setEnhancedEditPrompt('');

      const result = await influencerService.editImage(
        originalPath.replace('/public/', 'public/'),
        prompt.trim(),
        strength
      );

      setEditedImagePath(result.path);
      setEnhancedEditPrompt(result.enhanced_prompt || '');
    } catch (err) {
      setError(err.message || 'Failed to edit environment');
      console.error('Edit error:', err);
    } finally {
      setIsEditing(false);
    }
  };

  const handleConfirmEdits = async () => {
    if (!editedImagePath || !filename) return;

    try {
      setIsSaving(true);
      setError('');

      await influencerService.replaceEnvironment(
        filename,
        editedImagePath.replace('/public/', 'public/'),
        environmentName
      );

      if (onNavigate) onNavigate('environment-library');
    } catch (err) {
      setError(err.message || 'Failed to save environment');
      console.error('Save error:', err);
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    if (onNavigate) onNavigate('environment-library');
  };

  if (!originalPath || !environmentName || !filename) {
    return null;
  }

  return (
    <div className="edit-page">
      <div className="edit-header">
        <div className="edit-header-left">
          <button className="xbtn xbtn-md xbtn-secondary" onClick={handleCancel}>
            <i className="fas fa-arrow-left"></i> Back to Library
          </button>
          <div>
            <h1>Edit Environment</h1>
            <p className="edit-name">{environmentName}</p>
          </div>
        </div>
      </div>

      <div className="edit-layout">
        {/* Left - Edit Controls */}
        <div className="edit-controls-panel">
          <div className="form-card">
            <h2>Edit Settings</h2>

            <div className="form-section">
              <div className="form-group">
                <label>Edit Prompt</label>
                <textarea
                  placeholder="Describe how you want to modify the environment... (e.g., 'add rain effect', 'change to sunset lighting')"
                  value={prompt}
                  onChange={(e) => setPrompt(e.target.value)}
                  rows="4"
                  disabled={isEditing || isSaving}
                />
                <small className="field-hint">Be specific about the changes you want to make</small>
              </div>
            </div>

            <div className="form-section">
              <div className="form-group">
                <label>Modification Strength: {strength.toFixed(2)}</label>
                <input
                  type="range"
                  min="0.3"
                  max="0.9"
                  step="0.05"
                  value={strength}
                  onChange={(e) => setStrength(parseFloat(e.target.value))}
                  disabled={isEditing || isSaving}
                  className="range-slider"
                />
                <small className="field-hint">Lower = subtle changes, Higher = dramatic changes</small>
              </div>
            </div>

            {error && (
              <div className="message error">
                <i className="fas fa-exclamation-circle"></i>
                {error}
              </div>
            )}

            {enhancedEditPrompt && (
              <div className="enhanced-prompt">
                <strong>Enhanced Edit Instruction:</strong>
                <p>{enhancedEditPrompt}</p>
              </div>
            )}

            <div className="edit-action-buttons">
              <button
                className="xbtn xbtn-md xbtn-primary"
                onClick={handleEditImage}
                disabled={!prompt.trim() || isEditing || isSaving}
              >
                {isEditing ? (
                  <><i className="fas fa-spinner fa-spin"></i> Editing...</>
                ) : (
                  <><i className="fas fa-magic"></i> Generate Edit</>
                )}
              </button>

              {editedImagePath && (
                <>
                  <button
                    className="xbtn xbtn-md xbtn-success"
                    onClick={handleConfirmEdits}
                    disabled={isSaving}
                  >
                    {isSaving ? (
                      <><i className="fas fa-spinner fa-spin"></i> Saving...</>
                    ) : (
                      <><i className="fas fa-check"></i> Confirm & Save</>
                    )}
                  </button>
                  <button className="xbtn xbtn-md xbtn-secondary" onClick={handleCancel} disabled={isSaving}>
                    <i className="fas fa-times"></i> Cancel
                  </button>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Right - Image Comparison */}
        <div className="edit-preview-panel">
          <div className="form-card">
            <h2>Preview</h2>
            <div className="edit-comparison">
              <div className="comparison-column">
                <h3>Original Environment</h3>
                <div className="comparison-image">
                  <img src={getInfluencerImageUrl(originalPath)} alt={environmentName} />
                </div>
              </div>
              <div className="comparison-column">
                <h3>
                  Edited Environment
                  {editedImagePath && <span className="preview-badge">Preview</span>}
                </h3>
                <div className="comparison-image">
                  {isEditing ? (
                    <div className="comparison-placeholder">
                      <i className="fas fa-spinner fa-spin fa-2x"></i>
                      <p>Generating edited version...</p>
                      <small>This may take 30-60 seconds</small>
                    </div>
                  ) : editedImagePath ? (
                    <img src={getInfluencerImageUrl(editedImagePath)} alt="Edited" />
                  ) : (
                    <div className="comparison-placeholder">
                      <p>Edited version will appear here</p>
                      <small>Enter a prompt and click "Generate Edit"</small>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default EnvironmentEdit;
